import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiFetch } from '../services/apiHelper';
import { useAuth } from './AuthContext';

const FeatureFlagContext = createContext();

export const useFeatureFlags = () => useContext(FeatureFlagContext);

export const FeatureFlagProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [flags, setFlags] = useState({
    parking: true,
    commuteBuddy: true,
    outstation: true,
    tours: true,
    // Revenue-uplift features (default false until backend confirms)
    surgeAcceptUi: false,
    premiumDriverFilter: false,
    // Engagement levers — flipped on for re-engagement campaign (900-user base)
    loyaltyTiers: true,
    ridePasses: true,
    referralFunnel: true,
    // Streak/daily-reward card on home — backend DailyRewardService is production-ready
    streakRewards: true,
    rideInsurance: false,
    rideAddons: false,
    splitPayment: false,
    corporateConcierge: false,
    adsEnabled: false,
    bikeAutoCategories: false,
    airportFixedFare: false,
    // C4: unified native + ONDC quote list. Off by default; flip on when the
    // ONDC provider catalog is broad enough that the extra latency is worth it.
    unifiedBooking: false,
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    const controller = new AbortController();
    (async () => {
      try {
        const data = await apiFetch('/api/v1/config/features', { signal: controller.signal });
        setFlags(prev => ({ ...prev, ...data }));
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Failed to load feature flags, using defaults');
        }
      }
    })();
    return () => controller.abort();
  }, [isAuthenticated]);

  const isFeatureEnabled = (name) => flags[name] !== false;

  const refreshFlags = async () => {
    try {
      const data = await apiFetch('/api/v1/config/features');
      setFlags(prev => ({ ...prev, ...data }));
    } catch { /* ignore */ }
  };

  return (
    <FeatureFlagContext.Provider value={{ flags, isFeatureEnabled, refreshFlags }}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
